import { omit } from 'lodash';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import React, { useState, useCallback, useMemo, useEffect } from 'react';

import {
	GoogleReCaptchaProvider,
	useGoogleReCaptcha
  } from 'react-google-recaptcha-v3';

import PatientForm from './PatientForm';
import Header from '../../components/Header';
import Container from '../../components/Container';

import {
	getDefaultValueByFieldType,
	getMultipleChoiceGradeDefaultValue,
	getFieldName,
	getImageFileContainerId,
	MAX_FILE_FIELD_LENGTH
} from '../../utils/field';

import * as FileUtils from '../../utils/file';
import { signature } from '../../processes/patient-form';
import * as NavigationUtils from '../../utils/navigation';
import * as DomUtils from '../../utils/dom';
import { usePatientFormContext } from '../../contexts/PatientForm';
import { useWindowDimensions } from '../../utils/windowDimensionSize';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';

function PatientFormContainer() {
	const params = useParams();
	const location = useLocation();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const queryParams = new URLSearchParams(location.search);
	const cameFromSignature = !!queryParams.get('cameFromSignature');
	const history = useHistory();

	const { form, code, setInfo } = usePatientFormContext();
	const [loading, setLoading] = useState(false);
	const { width } = useWindowDimensions();

	const isDesktop = useMemo(() => width >= 600, [width]);
	const [fieldsGroupedByPage, setFieldsGroupedByPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [currentPage, setCurrentPage] = useState(1);

	const initialValuesMounted = useMemo(() => (
		(form?.fields || []).reduce((acc, field) => {
			if (field.type === 'PAGE') {
				return acc;
			}

			const defaultValueByField = field.type === 'MULTIPLE_CHOICE_GRADE' ? getMultipleChoiceGradeDefaultValue(field)  : getDefaultValueByFieldType(field.type);

			return {
				...acc,
				[`${field.type}_${field.id}`]: field.answer || defaultValueByField
			};
		}, {})
	), [form?.fields]);

	const getRecaptchaToken = useCallback(async () => {
		if (!executeRecaptcha) {
			return Promise.resolve(null);
		}

		const token = await executeRecaptcha('SIGNBOX_FORM');

		return token;
	}, [executeRecaptcha]);

	const {
		handleSubmit,
		handleChange,
		isValid,
		setFieldValue,
		values
	} = useFormik({
		enableReintialize: true,
		initialValues: initialValuesMounted,
		onSubmit: async values => {
			const requiredFieldNotFilled = form.fields.find(field => {
				const fieldName = getFieldName(field);
				const multiSelectFields = ['FILE', 'CHECKBOX'];
				const oneOptionSelectFields = ['TEXT', 'RADIO'];
				const matrizSelectFields = ['MULTIPLE_CHOICE_GRADE'];

				if (!field.required) {
					return false;
				}

				if (multiSelectFields.includes(field.type) && !values[fieldName].length) {
					return true;
				}

				if (oneOptionSelectFields.includes(field.type) && !values[fieldName]) {
					return true;
				}

				if (matrizSelectFields.includes(field.type)) {
					const hasFieldsNotAnswered = !Object.values(values[fieldName]).length || Object.values(values[fieldName]).some(item => !item);

					return hasFieldsNotAnswered;
				}

				return false;
			});

			if (requiredFieldNotFilled) {
				toast.error(`A pergunta "${requiredFieldNotFilled.title}" é obrigatória.`);
				return;
			}

			const hasFieldFilled = form.fields.some(field => {
				const fieldName = getFieldName(field);
				const multiSelectFields = ['FILE', 'CHECKBOX'];
				const oneOptionSelectFields = ['TEXT', 'RADIO'];
				const matrizSelectFields = ['MULTIPLE_CHOICE_GRADE'];

				if (multiSelectFields.includes(field.type) && values[fieldName].length) {
					return true;
				}

				if (oneOptionSelectFields.includes(field.type) && values[fieldName]) {
					return true;
				}

				if (matrizSelectFields.includes(field.type)) {
					const hasFieldAnswered = Object.values(values[fieldName]).some(item => !!item);

					return hasFieldAnswered;
				}

				return false;
			});

			if (!hasFieldFilled) {
				toast.error('O formulário precisa ter pelo menos 1 resposta.');
				return;
			};

			const formData = {
				id: form.id,
				code,
				cpf: form.pass_code,
				...getObjectData(values)
			};

			formData.recaptcha_token = await getRecaptchaToken();

			if (form.signature_url) {
				formData.signature_file = await FileUtils.getBase64Blob(form?.signature_url);
			}

			setLoading(true);

			const { data: pdfUrl } = await signature(formData);

			if (pdfUrl) {
				toast.success('Formulário enviado com sucesso.');

				setInfo(data => ({
					...data,
					form: {
						...data.form,
						pdf_url: pdfUrl
					}
				}))

				NavigationUtils.navigate(history, `/forms/signature-success/${code}`);
			} else {
				toast.error('Não foi possível salvar o formulário. Tente novamente em instantes.');
			}

			setLoading(false);
		}
	});

	const getObjectData = values => {
		return form.fields.reduce((acc, field) => {
			const fieldName = getFieldName(field);
			const fieldAnswer = values[fieldName];

			if (field.type === 'CHECKBOX' && fieldAnswer?.length) {
				const newAnswers = fieldAnswer.map(optionId => ({
					signbox_patient_form_field_option_id: ~~optionId,
					signbox_patient_form_field_id: field.id
				}));

				acc.answers = acc.answers.concat(newAnswers);
			}

			if (field.type === 'FILE' && fieldAnswer.length) {
				acc.files = acc.files.concat(fieldAnswer);
			}

			if (field.type === 'TEXT' && fieldAnswer) {
				acc.answers.push({
					signbox_patient_form_field_id: field.id,
					text: fieldAnswer
				});
			}

			if (field.type === 'RADIO' && fieldAnswer) {
				acc.answers.push({
					signbox_patient_form_field_id: field.id,
					signbox_patient_form_field_option_id: ~~fieldAnswer
				});
			}

			if (field.type === 'MULTIPLE_CHOICE_GRADE' && fieldAnswer) {
				const fieldsAnswered = Object.keys(fieldAnswer).reduce((accumulator, optionId) => {
					const answer = fieldAnswer[optionId];

					if (!answer) {
						return accumulator;
					}

					accumulator.push({
						signbox_patient_form_field_id: field.id,
						signbox_patient_form_field_option_id: optionId,
						signbox_patient_form_field_option_column_id: answer
					})

					return accumulator;
				}, []);

				acc.answers = acc.answers.concat(fieldsAnswered);
			}

			return acc;
		}, { answers: [], files: [] });
	};

	const uploadFiles = useCallback(async ({ event, field }) => {
		if (!event.target.files) {
			return;
		}

		const fieldName = getFieldName(field);
		const currentFiles = values[fieldName] || []
		const filePromises = [];

		const files = Array.from(event.target.files);

		if (files.length + currentFiles.length >= MAX_FILE_FIELD_LENGTH) {
			toast.warn(`Só é possível inserir ${MAX_FILE_FIELD_LENGTH} arquivos`);
			return;
		}

		for (const fileIndex in files) {
			const file = FileUtils.renameFile(files[fileIndex], `${field.id}_FORM_FIELD_QUESTION_${files[fileIndex]?.name}`);

			const filePromise = await new Promise(async resolve => {
				file.signbox_patient_form_field_id = field.id;

				resolve(file);
			});

			filePromises.push(filePromise);
		}

		const newFilesMounted = await Promise.all(filePromises);

		setFieldValue(fieldName, currentFiles.concat(newFilesMounted));
	}, [values, setFieldValue]);

	const removeFile = useCallback(async (field, fileIndexToRemove) => {
		const fieldName = getFieldName(field);

		const newFiles = values[fieldName].filter((file ,index) => index !== fileIndexToRemove);

		setFieldValue(fieldName, newFiles)
	}, [values, setFieldValue]);

	const onAttachFileClick = field => {
		const fileContainer = document.getElementById(getImageFileContainerId(field))

		fileContainer.click();
	}

	const onClickSignatureBox = useCallback(() => {
		const fieldsWithAnswers = form?.fields.map(field => {
			const fieldName = getFieldName(field);

			return {
				...field,
				answer: values[fieldName]
			};
		});

		setInfo(data => ({
			...data,
			form: {
				...data.form,
				fields: fieldsWithAnswers
			}
		}));

		return NavigationUtils.navigate(history, `/forms/signature/${code}`);
	}, [values, setInfo, code, history, form?.fields]);

	const handleClearForm = useCallback(() => {
		const fieldsCleared = form?.fields.map(field => omit(field, ['answer']));

		setInfo(data => ({
			...data,
			form: {
				...data.form,
				fields: fieldsCleared
			}
		}));

		form.fields.forEach(field => {
			const fieldName = getFieldName(field);

			setFieldValue(fieldName, getDefaultValueByFieldType(field.type))
		});

		setCurrentPage(1);

		DomUtils.scrollTop();
	}, [form?.fields, setFieldValue, setInfo, setCurrentPage])

	const fieldsByPage = useMemo(() => {
		return fieldsGroupedByPage?.[currentPage]?.fields || [];
	}, [fieldsGroupedByPage, currentPage]);

	const mountFieldsByPage = useCallback(() => {
		let pagesCreated = 1;

		const fieldsPerPage = form.fields.reduce((acc, field) => {
			if (field.type === 'PAGE') {
				pagesCreated++;

				acc[pagesCreated] = {
					id: field.id,
					title: field.title,
					description: field.description,
					fields: []
				};
			} else {
				acc[pagesCreated].fields.push(field);
			}


			return acc;
		}, {
			1: {
				title: form.title,
				description: form.description,
				fields: []
			}
		});

		setFieldsGroupedByPage(fieldsPerPage);

		if (cameFromSignature) {
			setCurrentPage(pagesCreated);
		}

		setTotalPages(pagesCreated);
	}, [form?.title, form?.description, form?.fields, cameFromSignature])

	const isValidPageForm = useCallback(() => {
		const requiredFieldNotFilled = fieldsByPage.find(field => {
			const fieldName = getFieldName(field);
			const multiSelectFields = ['FILE', 'CHECKBOX'];
			const oneOptionSelectFields = ['TEXT', 'RADIO'];
			const matrizSelectFields = ['MULTIPLE_CHOICE_GRADE'];

			if (!field.required) {
				return false;
			}

			if (multiSelectFields.includes(field.type) && !values[fieldName].length) {
				return true;
			}

			if (oneOptionSelectFields.includes(field.type) && !values[fieldName]) {
				return true;
			}

			if (matrizSelectFields.includes(field.type)) {
				const hasFieldsNotAnswered = !Object.values(values[fieldName]).length || Object.values(values[fieldName]).some(item => !item);

				return hasFieldsNotAnswered;
			}

			return false;
		});

		if (requiredFieldNotFilled) {
			toast.error(`A pergunta "${requiredFieldNotFilled.title}" é obrigatória.`);
			return;
		}

		return true;
	}, [fieldsByPage, values]);

	const onContinueStepClick = useCallback(() => {
		if (currentPage === totalPages) {
			return;
		}

		if (!isValidPageForm()) {
			return;
		}

		setCurrentPage(currentPage + 1);

		DomUtils.scrollTop();
	}, [currentPage, totalPages, isValidPageForm]);

	const onBackStepClick = useCallback(() => {
		if (currentPage === 1) {
			return;
		}

		setCurrentPage(currentPage - 1);

		DomUtils.scrollTop();
	}, [currentPage]);

	useEffect(() => {
		if (!form?.fields?.length) {
			return NavigationUtils.navigate(history, `/privacy-policy/${code || params?.code}`);
		}

		mountFieldsByPage();
	}, [form?.fields, mountFieldsByPage, history, code, params?.code])

    return (
		<Container bgColor="#F5F5F5">
			<Header/>

			<PatientForm
				form={form}
				loading={loading}
				onSubmit={handleSubmit}
				handleChange={handleChange}
				currentPage={currentPage}
				totalPages={totalPages}
				values={values}
				isValidForm={isValid}
				onClearForm={handleClearForm}
				isDesktop={isDesktop}
				onClickSignatureBox={onClickSignatureBox}
				uploadFiles={uploadFiles}
				fieldsByPage={fieldsByPage}
				onBackStepClick={onBackStepClick}
				onContinueStepClick={onContinueStepClick}
				onAttachFileClick={onAttachFileClick}
				removeFile={removeFile}
			/>
		</Container>
	);
}

const PatientFormWithRecaptchaProvider = () => {
	return (
	  	<GoogleReCaptchaProvider reCaptchaKey="6Lfb2lQdAAAAAEY88Q5VljbDmGjxAI1Kt5wSDCxN">
			<PatientFormContainer />
		</GoogleReCaptchaProvider>
	);
};

export default PatientFormWithRecaptchaProvider;
